import { useTranslate } from "#src/hooks";
import React, { useEffect, useMemo, RefObject, CSSProperties } from "react";
import { Card } from "antd";
import type { CardProps } from "antd";
import { CascadingText } from "components/CascadingText";
import TextArea, { TextAreaProps, TextAreaRef } from "antd/es/input/TextArea";

export const FONT_PREVIEW_WIDTH = 200;

// Simple cache to prevent loading the same font multiple times
const loadedFonts = new Set<string>();

// Generate a stable font family name based on the font URL or File.
// Exported so canvas-based text measurement (e.g. line-height) can target the
// exact same registered FontFace the preview renders with.
export const getFontFamilyName = (font: string | File): string => {
  if (!font) return "fallback";
  let key: string;
  if (font instanceof File) {
    key = font.name + font.size + font.lastModified;
  } else {
    key = font;
  }
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    const char = key.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return `PreviewFont-${Math.abs(hash)}`;
};

// Load font if not already loaded
const loadFontIfNeeded = async (fontFamilyName: string, fontUrl: string) => {
  if (loadedFonts.has(fontFamilyName)) {
    return; // Font already loaded
  }

  try {
    const fontFace = new FontFace(fontFamilyName, `url(${fontUrl})`);
    await fontFace.load();
    document.fonts.add(fontFace);
    loadedFonts.add(fontFamilyName);
  } catch (error) {
    console.error("Error loading font:", fontFamilyName, error);
  }
};

interface FontPreviewCardProps extends CardProps {
  font: string | File;
  title: string;
  style?: CSSProperties;
}

export interface FontPreviewTextProps {
  font: string | File;
  text?: string;
  style?: CSSProperties;
  animated?: boolean;
  editable?: boolean;
  editableProps?: Omit<TextAreaProps, "style">;
  editableRef?: RefObject<TextAreaRef>;
  /**
   * Style applied to the text-bearing element itself (the editable TextArea, or
   * a wrapper around static text) rather than the outer box. Used to offset the
   * text within its box (e.g. trimming line-box leading) without moving the box.
   */
  contentStyle?: CSSProperties;
  /**
   * Make the outer wrapper fill its parent (width/height 100%) so a `style` with
   * `height: "100%"` on the inner text element resolves against a sized box (the
   * wrapper otherwise shrink-wraps the content). Used for the editor's live
   * resize, where the text must track the box.
   */
  fillContainer?: boolean;
}

export const FontPreviewText: React.FC<FontPreviewTextProps> = ({
  font,
  text,
  style,
  animated,
  editable,
  editableProps,
  editableRef,
  fillContainer,
  contentStyle,
}) => {
  const fontFamilyName = useMemo(() => getFontFamilyName(font), [font]);

  // Handle File objects by creating object URLs
  const fontUrl = useMemo(() => {
    if (font instanceof File) {
      return URL.createObjectURL(font);
    }
    return font;
  }, [font]);

  useEffect(() => {
    return () => {
      if (font instanceof File && fontUrl) {
        URL.revokeObjectURL(fontUrl);
      }
    };
  }, [font, fontUrl]);

  // Load font directly
  useEffect(() => {
    if (!fontUrl || !fontFamilyName) return;

    loadFontIfNeeded(fontFamilyName, fontUrl).catch((error) =>
      console.error("Error loading font", error)
    );
  }, [fontUrl, fontFamilyName]);

  const previewStyle: CSSProperties = {
    fontFamily: `${fontFamilyName}, sans-serif`,
    fontSize: "48px",
    textAlign: "center",
    padding: animated ? "unset" : "10px",
    ...style,
  };

  const renderAnimatedText = () => {
    return (
      <div style={previewStyle}>
        <CascadingText
          style={{
            whiteSpace: style?.whiteSpace ?? "normal",
            maxWidth: style ? style.width : "unset",
            maxHeight: style ? style.height : "unset",
            ...contentStyle,
          }}
        >
          {text ?? ""}
        </CascadingText>
      </div>
    );
  };

  const renderEditableText = () => {
    const hasFixedHeight = style?.height && style.height !== "fit-content";
    return (
      <TextArea
        ref={editableRef}
        variant="borderless"
        autoSize={hasFixedHeight ? false : { minRows: 1 }}
        defaultValue={text ?? ""}
        style={{
          ...previewStyle,
          minHeight: "unset",
          resize: "none",
          ...contentStyle,
        }}
        {...editableProps}
      />
    );
  };

  const innerComponent = () => {
    if (editable) {
      return renderEditableText();
    } else if (animated) {
      return renderAnimatedText();
    } else {
      return (
        <div style={previewStyle}>
          {contentStyle ? (
            <div style={contentStyle}>{text ?? "Aa"}</div>
          ) : (
            (text ?? "Aa")
          )}
        </div>
      );
    }
  };

  const editPreviewStyle: CSSProperties = useMemo(
    () => ({
      ...(fillContainer ? { width: "100%", height: "100%" } : {}),
      ...(editable && style
        ? {
            height: style.height,
            display: style.display,
            flexDirection: style.flexDirection,
            justifyContent: style.justifyContent,
          }
        : {}),
    }),
    [style, editable, fillContainer]
  );

  return (
    <div data-testid="text-font-preview" style={editPreviewStyle}>
      {innerComponent()}
    </div>
  );
};

FontPreviewText.displayName = "FontPreviewText";

export const FontPreviewCard: React.FC<FontPreviewCardProps> = ({
  font,
  title,
  actions,
  style,
  ...props
}) => {
  const t = useTranslate();
  return (
    <Card
      size="small"
      style={{ ...style, width: FONT_PREVIEW_WIDTH }}
      styles={{
        actions: { padding: 10, paddingBottom: 0 },
        body: {
          padding: 0,
        },
      }}
      cover={
        <FontPreviewText
          text={t("brandkit.components.FontPreview.aa")}
          font={font}
        />
      }
      actions={actions}
      {...props}
    >
      <Card.Meta description={title} />
    </Card>
  );
};
